import { ref, type Ref } from 'vue'
import type { CropRegion, DragHandle, EditMode } from '../types'

export function useMouseInteraction(
  canvas: Ref<HTMLCanvasElement | null>,
  editMode: Ref<EditMode>,
  cropRegions: Ref<CropRegion[]>,
  selectedRegionId: Ref<string | null>,
  canvasWidth: Ref<number>,
  canvasHeight: Ref<number>,
  setActiveRegion: (regionId: string) => void,
  draw: () => void,
  image: Ref<HTMLImageElement | null>,
  externalZoomedPreviewCanvas?: Ref<HTMLCanvasElement | null>,
  imageDisplayParams?: Ref<any>
) {
  // Mouse interaction state
  const isDragging = ref(false)
  const dragHandle = ref<DragHandle>(null)
  const dragCornerIndex = ref(-1)
  const dragEdgeIndex = ref(-1)
  const selectedRegionIndex = ref<number | null>(null)
  const canvasRect = ref<DOMRect | null>(null)

  // Zoomed preview state
  const showZoomedPreview = ref(false)
  const zoomedPreviewPosition = ref({ x: 0, y: 0 })
  const zoomedPreviewCanvas = externalZoomedPreviewCanvas || ref<HTMLCanvasElement | null>(null)
  const zoomedPreviewSize = 150

  function updateCanvasRect() {
    if (canvas.value) {
      canvasRect.value = canvas.value.getBoundingClientRect()
    }
  }

  function handleMouseDown(e: MouseEvent) {
    if (!canvas.value) return

    updateCanvasRect()
    const mouseX = e.clientX - (canvasRect.value?.left || 0)
    const mouseY = e.clientY - (canvasRect.value?.top || 0)

    if (editMode.value === 'crop') {
      // Find which region was clicked
      let clickedRegionIndex = -1
      let clickedRegion = null

      for (let regionIndex = 0; regionIndex < cropRegions.value.length; regionIndex++) {
        const region = cropRegions.value[regionIndex]

        // Check corner handles first (highest priority) - now circular
        for (let i = 0; i < region.vertices.length; i++) {
          const dx = mouseX - region.vertices[i].x
          const dy = mouseY - region.vertices[i].y
          const distance = Math.sqrt(dx * dx + dy * dy)

          // Adjusted hit detection for circular corner handles
          const isActive = region.id === selectedRegionId.value
          const handleRadius = isActive ? 8 : 6
          const hitRadius = handleRadius + 4 // Add some padding for easier clicking

          if (distance < hitRadius) {
            clickedRegionIndex = regionIndex
            clickedRegion = region
            dragCornerIndex.value = i
            dragHandle.value = 'corner'
            isDragging.value = true
            selectedRegionIndex.value = regionIndex
            setActiveRegion(region.id)
            canvas.value.style.cursor = 'move'

            // Show zoomed preview for corner handles
            showZoomedPreview.value = true
            updateCanvasRect() // Ensure we have the latest canvas rect

            // Add document-level event listeners for dragging outside canvas
            document.addEventListener('mousemove', handleDocumentMouseMove)
            document.addEventListener('mouseup', handleDocumentMouseUp)

            // Ensure canvas connection is available (it might be recreated due to v-if)
            setTimeout(() => {
              updateZoomedPreview(mouseX, mouseY)
            }, 10) // Small delay to ensure DOM is updated

            e.preventDefault()
            return
          }
        }

        // Check edge handles (if region is active) - now pill-shaped
        if (region.id === selectedRegionId.value) {
          for (let i = 0; i < region.vertices.length; i++) {
            const currentVertex = region.vertices[i]
            const nextVertex = region.vertices[(i + 1) % region.vertices.length]

            const midX = (currentVertex.x + nextVertex.x) / 2
            const midY = (currentVertex.y + nextVertex.y) / 2

            // Calculate edge direction for pill orientation
            const edgeX = nextVertex.x - currentVertex.x
            const edgeY = nextVertex.y - currentVertex.y
            const edgeLength = Math.sqrt(edgeX * edgeX + edgeY * edgeY)

            // Normalize edge direction
            const normalizedX = edgeX / edgeLength
            const normalizedY = edgeY / edgeLength

            // Transform mouse position to pill's local coordinate system
            const relativeX = mouseX - midX
            const relativeY = mouseY - midY

            // Rotate to align with pill orientation
            const angle = Math.atan2(normalizedY, normalizedX)
            const cos = Math.cos(-angle)
            const sin = Math.sin(-angle)
            const localX = relativeX * cos - relativeY * sin
            const localY = relativeX * sin + relativeY * cos

            // Check if point is inside pill shape (20x8 rounded rectangle)
            const pillWidth = 20
            const pillHeight = 8
            const pillRadius = pillHeight / 2

            // Check if inside the pill bounds with some padding for easier clicking
            const padding = 2
            const isInsidePill =
              Math.abs(localX) <= pillWidth / 2 + padding &&
              Math.abs(localY) <= pillRadius + padding

            if (isInsidePill) {
              clickedRegionIndex = regionIndex
              clickedRegion = region
              dragHandle.value = 'edge'
              dragEdgeIndex.value = i
              isDragging.value = true
              selectedRegionIndex.value = regionIndex
              canvas.value.style.cursor = 'move'

              // Add document-level event listeners for dragging outside canvas
              document.addEventListener('mousemove', handleDocumentMouseMove)
              document.addEventListener('mouseup', handleDocumentMouseUp)

              // No zoomed preview for edge handles - only for corner handles

              e.preventDefault()
              return
            }
          }
        }

        // Check if clicking inside the region (for moving)
        if (isPointInPolygon(mouseX, mouseY, region.vertices)) {
          clickedRegionIndex = regionIndex
          clickedRegion = region
        }
      }

      // If clicked inside a region, set up for moving
      if (clickedRegion && clickedRegionIndex >= 0) {
        dragHandle.value = 'move'
        isDragging.value = true
        selectedRegionIndex.value = clickedRegionIndex
        setActiveRegion(clickedRegion.id)
        canvas.value.style.cursor = 'move'

        // Add document-level event listeners for dragging outside canvas
        document.addEventListener('mousemove', handleDocumentMouseMove)
        document.addEventListener('mouseup', handleDocumentMouseUp)

        e.preventDefault()
      }
    }
  }

  function handleMouseMove(e: MouseEvent) {
    if (!canvas.value || !canvasRect.value) return

    const mouseX = e.clientX - canvasRect.value.left
    const mouseY = e.clientY - canvasRect.value.top

    // Update cursor based on what we're hovering over (only when not dragging)
    if (!isDragging.value && editMode.value === 'crop') {
      updateCursorOnHover(mouseX, mouseY)
    }

    handleMouseMoveLogic(mouseX, mouseY)
  }

  function updateCursorOnHover(mouseX: number, mouseY: number) {
    if (!canvas.value) return

    let cursorStyle = 'crosshair' // Default cursor for crop mode

    // Check if hovering over any handles
    for (const region of cropRegions.value) {
      // Check corner handles
      for (let i = 0; i < region.vertices.length; i++) {
        const vertex = region.vertices[i]
        const dx = mouseX - vertex.x
        const dy = mouseY - vertex.y
        const distance = Math.sqrt(dx * dx + dy * dy)

        const isActive = region.id === selectedRegionId.value
        const handleRadius = isActive ? 8 : 6
        const hitRadius = handleRadius + 4

        if (distance < hitRadius) {
          // Set cursor based on corner position (assuming rectangular region)
          // Corner 0: top-left, 1: top-right, 2: bottom-right, 3: bottom-left
          const cornerCursors = ['nw-resize', 'ne-resize', 'se-resize', 'sw-resize']
          cursorStyle = cornerCursors[i % 4]
          break
        }
      }

      // Check edge handles (if region is active)
      if (region.id === selectedRegionId.value && cursorStyle === 'crosshair') {
        for (let i = 0; i < region.vertices.length; i++) {
          const currentVertex = region.vertices[i]
          const nextVertex = region.vertices[(i + 1) % region.vertices.length]

          const midX = (currentVertex.x + nextVertex.x) / 2
          const midY = (currentVertex.y + nextVertex.y) / 2

          // Calculate edge direction for pill orientation
          const edgeX = nextVertex.x - currentVertex.x
          const edgeY = nextVertex.y - currentVertex.y
          const edgeLength = Math.sqrt(edgeX * edgeX + edgeY * edgeY)

          const normalizedX = edgeX / edgeLength
          const normalizedY = edgeY / edgeLength

          // Transform mouse position to pill's local coordinate system
          const relativeX = mouseX - midX
          const relativeY = mouseY - midY

          const angle = Math.atan2(normalizedY, normalizedX)
          const cos = Math.cos(-angle)
          const sin = Math.sin(-angle)
          const localX = relativeX * cos - relativeY * sin
          const localY = relativeX * sin + relativeY * cos

          // Check if inside pill shape
          const pillWidth = 20
          const pillHeight = 8
          const pillRadius = pillHeight / 2
          const padding = 2

          const isInsidePill =
            Math.abs(localX) <= pillWidth / 2 + padding && Math.abs(localY) <= pillRadius + padding

          if (isInsidePill) {
            // Set cursor based on edge orientation
            const absAngle = Math.abs(angle)
            if (absAngle < Math.PI / 4 || absAngle > (3 * Math.PI) / 4) {
              cursorStyle = 'ns-resize' // Horizontal edge
            } else {
              cursorStyle = 'ew-resize' // Vertical edge
            }
            break
          }
        }
      }

      // Check if hovering inside region (for move cursor)
      if (cursorStyle === 'crosshair' && isPointInPolygon(mouseX, mouseY, region.vertices)) {
        cursorStyle = 'move'
      }

      // Break if we found a handle
      if (cursorStyle !== 'crosshair') break
    }

    canvas.value.style.cursor = cursorStyle
  }

  function handleMouseMoveLogic(mouseX: number, mouseY: number) {
    if (isDragging.value && editMode.value === 'crop' && selectedRegionIndex.value !== null) {
      const region = cropRegions.value[selectedRegionIndex.value]
      if (!region) return

      const displayWidth = canvasWidth.value || 1200
      const displayHeight = canvasHeight.value || 800

      if (dragHandle.value === 'corner' && dragCornerIndex.value >= 0) {
        // Move corner handle - clamp to canvas edges when dragging outside
        const clampedX = Math.max(0, Math.min(displayWidth, mouseX))
        const clampedY = Math.max(0, Math.min(displayHeight, mouseY))

        region.vertices[dragCornerIndex.value].x = clampedX
        region.vertices[dragCornerIndex.value].y = clampedY

        // Update zoomed preview with original mouse position for smooth tracking
        updateZoomedPreview(mouseX, mouseY)
      } else if (dragHandle.value === 'edge' && dragEdgeIndex.value >= 0) {
        // Move edge - adjust both vertices of the edge
        const currentIndex = dragEdgeIndex.value
        const nextIndex = (dragEdgeIndex.value + 1) % region.vertices.length

        const currentVertex = region.vertices[currentIndex]
        const nextVertex = region.vertices[nextIndex]

        // Calculate the original edge vector
        const edgeVectorX = nextVertex.x - currentVertex.x
        const edgeVectorY = nextVertex.y - currentVertex.y

        // Calculate perpendicular vector for edge movement
        const perpX = -edgeVectorY
        const perpY = edgeVectorX
        const perpLength = Math.sqrt(perpX * perpX + perpY * perpY)

        if (perpLength > 0) {
          const normalizedPerpX = perpX / perpLength
          const normalizedPerpY = perpY / perpLength

          // Calculate how much to move the edge
          const edgeMidX = (currentVertex.x + nextVertex.x) / 2
          const edgeMidY = (currentVertex.y + nextVertex.y) / 2

          const deltaX = mouseX - edgeMidX
          const deltaY = mouseY - edgeMidY

          // Project the mouse movement onto the perpendicular direction
          const projectedDistance = deltaX * normalizedPerpX + deltaY * normalizedPerpY

          // Move both vertices
          const moveX = normalizedPerpX * projectedDistance
          const moveY = normalizedPerpY * projectedDistance

          region.vertices[currentIndex].x = Math.max(
            0,
            Math.min(displayWidth, currentVertex.x + moveX)
          )
          region.vertices[currentIndex].y = Math.max(
            0,
            Math.min(displayHeight, currentVertex.y + moveY)
          )
          region.vertices[nextIndex].x = Math.max(0, Math.min(displayWidth, nextVertex.x + moveX))
          region.vertices[nextIndex].y = Math.max(0, Math.min(displayHeight, nextVertex.y + moveY))

          // No zoomed preview for edge handles - only for corner handles
        }
      } else if (dragHandle.value === 'move') {
        // Move entire region
        if (!region.lastMouseX || !region.lastMouseY) {
          region.lastMouseX = mouseX
          region.lastMouseY = mouseY
          return
        }

        const deltaX = mouseX - region.lastMouseX
        const deltaY = mouseY - region.lastMouseY

        // Compute region bounds to determine how far we can move without crossing edges
        let minX = Infinity
        let maxX = -Infinity
        let minY = Infinity
        let maxY = -Infinity
        for (const v of region.vertices) {
          if (v.x < minX) minX = v.x
          if (v.x > maxX) maxX = v.x
          if (v.y < minY) minY = v.y
          if (v.y > maxY) maxY = v.y
        }

        // Allowed delta ranges so the entire region stays within bounds
        const minAllowedDeltaX = -minX
        const maxAllowedDeltaX = displayWidth - maxX
        const minAllowedDeltaY = -minY
        const maxAllowedDeltaY = displayHeight - maxY

        const appliedDeltaX = Math.max(minAllowedDeltaX, Math.min(maxAllowedDeltaX, deltaX))
        const appliedDeltaY = Math.max(minAllowedDeltaY, Math.min(maxAllowedDeltaY, deltaY))

        // Move all vertices by the clamped translation (no per-vertex clamping → no shrinking)
        region.vertices.forEach((vertex) => {
          vertex.x = vertex.x + appliedDeltaX
          vertex.y = vertex.y + appliedDeltaY
        })

        // Advance last mouse by the applied movement so continued drags beyond the edge don't shrink
        region.lastMouseX = region.lastMouseX + appliedDeltaX
        region.lastMouseY = region.lastMouseY + appliedDeltaY
      }

      draw()
    }
  }

  function handleMouseUp() {
    isDragging.value = false
    dragHandle.value = null
    dragCornerIndex.value = -1
    dragEdgeIndex.value = -1

    // Hide zoomed preview
    showZoomedPreview.value = false

    // Remove document-level event listeners
    document.removeEventListener('mousemove', handleDocumentMouseMove)
    document.removeEventListener('mouseup', handleDocumentMouseUp)

    // Clear last mouse position for move operations
    cropRegions.value.forEach((region) => {
      delete region.lastMouseX
      delete region.lastMouseY
    })

    if (canvas.value) {
      canvas.value.style.cursor = 'crosshair'
    }
  }

  // Document-level mouse move handler for dragging outside canvas
  function handleDocumentMouseMove(e: MouseEvent) {
    if (!canvas.value || !canvasRect.value) return

    // Calculate mouse position relative to canvas
    const mouseX = e.clientX - canvasRect.value.left
    const mouseY = e.clientY - canvasRect.value.top

    // Call the existing handleMouseMove logic
    handleMouseMoveLogic(mouseX, mouseY)
  }

  // Document-level mouse up handler
  function handleDocumentMouseUp() {
    handleMouseUp()
  }

  // Cleanup function to remove document event listeners and reset cursor
  function cleanup() {
    try {
      document.removeEventListener('mousemove', handleDocumentMouseMove)
      document.removeEventListener('mouseup', handleDocumentMouseUp)

      // Reset cursor to default
      if (canvas.value) {
        canvas.value.style.cursor = 'default'
      }
    } catch (error) {
      console.warn('Error during mouse interaction cleanup:', error)
    }
  }

  // Helper function to check if a point is inside a polygon
  function isPointInPolygon(
    x: number,
    y: number,
    vertices: Array<{ x: number; y: number }>
  ): boolean {
    let inside = false
    for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
      if (
        vertices[i].y > y !== vertices[j].y > y &&
        x <
          ((vertices[j].x - vertices[i].x) * (y - vertices[i].y)) /
            (vertices[j].y - vertices[i].y) +
            vertices[i].x
      ) {
        inside = !inside
      }
    }
    return inside
  }

  // Create zoomed preview of the area around a handle
  function createZoomedPreview(centerX: number, centerY: number): string | null {
    if (!canvas.value || !image.value) {
      console.log('Missing canvas or image')
      return null
    }

    // Check if image is loaded
    if (!image.value.complete || image.value.naturalWidth === 0) {
      console.log('Image not loaded yet')
      return null
    }

    // Create a temporary canvas for the zoomed preview
    const previewCanvas = document.createElement('canvas')
    const previewCtx = previewCanvas.getContext('2d')
    if (!previewCtx) return null

    previewCanvas.width = zoomedPreviewSize
    previewCanvas.height = zoomedPreviewSize

    // Convert canvas coordinates to image coordinates (EXACT OLD IMPLEMENTATION)
    const scaleX = image.value.width / canvasWidth.value
    const scaleY = image.value.height / canvasHeight.value

    // Calculate center point in image coordinates
    const imageCenterX = centerX * scaleX
    const imageCenterY = centerY * scaleY

    // Fixed source size in image coordinates (not scaled by display)
    const imageSourceSize = 100 // Fixed size in image pixels

    // Calculate source bounds, allowing negative values for proper centering
    const imageSourceX = imageCenterX - imageSourceSize / 2
    const imageSourceY = imageCenterY - imageSourceSize / 2

    // Calculate what portion of the source area is actually within the image
    const clampedSourceX = Math.max(0, imageSourceX)
    const clampedSourceY = Math.max(0, imageSourceY)
    const clampedSourceRight = Math.min(image.value.width, imageSourceX + imageSourceSize)
    const clampedSourceBottom = Math.min(image.value.height, imageSourceY + imageSourceSize)

    const actualImageSourceWidth = clampedSourceRight - clampedSourceX
    const actualImageSourceHeight = clampedSourceBottom - clampedSourceY

    // Draw the zoomed area from the original image (clean, without handles/overlays)
    previewCtx.imageSmoothingEnabled = false // Pixelated zoom for precision

    // Fill with white background first (for edge cases)
    previewCtx.fillStyle = '#ffffff'
    previewCtx.fillRect(0, 0, zoomedPreviewSize, zoomedPreviewSize)

    // Calculate destination coordinates to properly center the mouse position
    // The mouse should always be at the center of the preview (crosshairs)
    const offsetX = imageSourceX - clampedSourceX // How much we're offset from the desired source
    const offsetY = imageSourceY - clampedSourceY

    const destX = (offsetX / imageSourceSize) * zoomedPreviewSize
    const destY = (offsetY / imageSourceSize) * zoomedPreviewSize
    const destWidth = (actualImageSourceWidth / imageSourceSize) * zoomedPreviewSize
    const destHeight = (actualImageSourceHeight / imageSourceSize) * zoomedPreviewSize

    // Only draw if we have actual image content

    if (actualImageSourceWidth > 0 && actualImageSourceHeight > 0) {
      previewCtx.drawImage(
        image.value,
        clampedSourceX,
        clampedSourceY,
        actualImageSourceWidth,
        actualImageSourceHeight,
        destX,
        destY,
        destWidth,
        destHeight
      )
    }

    // Draw crosshairs for precise alignment
    previewCtx.strokeStyle = '#ff0000'
    previewCtx.lineWidth = 1
    previewCtx.setLineDash([2, 2])

    // Vertical crosshair
    previewCtx.beginPath()
    previewCtx.moveTo(zoomedPreviewSize / 2, 0)
    previewCtx.lineTo(zoomedPreviewSize / 2, zoomedPreviewSize)
    previewCtx.stroke()

    // Horizontal crosshair
    previewCtx.beginPath()
    previewCtx.moveTo(0, zoomedPreviewSize / 2)
    previewCtx.lineTo(zoomedPreviewSize, zoomedPreviewSize / 2)
    previewCtx.stroke()

    previewCtx.setLineDash([])

    return previewCanvas.toDataURL('image/png', 1.0)
  }

  // Update zoomed preview position and content
  function updateZoomedPreview(mouseX: number, mouseY: number) {
    if (!canvas.value || !canvasRect.value) return

    // Position the preview to the side of the cursor
    const previewOffset = 20
    let previewX = mouseX + previewOffset
    let previewY = mouseY - zoomedPreviewSize / 2

    // Keep preview within viewport bounds
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    if (previewX + zoomedPreviewSize > viewportWidth - 20) {
      previewX = mouseX - zoomedPreviewSize - previewOffset
    }
    if (previewY < 20) {
      previewY = 20
    }
    if (previewY + zoomedPreviewSize > viewportHeight - 20) {
      previewY = viewportHeight - zoomedPreviewSize - 20
    }

    zoomedPreviewPosition.value = {
      x: previewX + canvasRect.value.left,
      y: previewY + canvasRect.value.top
    }

    // Create the zoomed preview content
    const previewData = createZoomedPreview(mouseX, mouseY)

    if (previewData && zoomedPreviewCanvas.value) {
      const img = new Image()
      img.onload = () => {
        if (zoomedPreviewCanvas.value) {
          const ctx = zoomedPreviewCanvas.value.getContext('2d')
          if (ctx) {
            ctx.clearRect(0, 0, zoomedPreviewSize, zoomedPreviewSize)
            ctx.drawImage(img, 0, 0)
          }
        }
      }
      img.src = previewData
    }
  }

  return {
    // State
    isDragging,
    dragHandle,
    dragCornerIndex,
    dragEdgeIndex,
    selectedRegionIndex,
    canvasRect,
    showZoomedPreview,
    zoomedPreviewPosition,
    zoomedPreviewCanvas,
    zoomedPreviewSize,

    // Methods
    updateCanvasRect,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    isPointInPolygon,
    updateZoomedPreview,
    cleanup
  }
}
